import React, { useState, useEffect } from 'react'
import { useApp } from '../../context/AppContext'
import { Shield, ArrowRight, Loader2, ArrowLeft } from 'lucide-react'

function OTPVerification({ phoneNumber, onBack, onOTPSubmit }) {
  const [otpCode, setOtpCode] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes
  const { verifyOTP, isLoading, sendOTP } = useApp()

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  const validateOTP = (code) => {
    // OTP should be 4-6 digits
    const otpRegex = /^\d{4,6}$/
    return otpRegex.test(code)
  }

  const handleOTPChange = (e) => {
    const value = e.target.value
    setOtpCode(value)
    setIsValid(validateOTP(value))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid) return

    try {
      await verifyOTP(phoneNumber, otpCode)
      // Call the parent component callback to proceed to profile step
      if (onOTPSubmit) {
        onOTPSubmit()
      }
    } catch (error) {
      console.error('Failed to verify OTP:', error)
    }
  }

  const handleResend = async () => {
    try {
      // Call backend to send a fresh OTP via WhatsApp
      await sendOTP(phoneNumber)
      setTimeLeft(300)
    } catch (error) {
      console.error('Failed to resend OTP:', error)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="flex justify-center">
      <div className="card">
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Verify Your Phone
          </h2>
          <p className="text-gray-600 text-sm">
            Enter the verification code sent to
          </p>
          <p className="text-primary-600 font-semibold">
            {phoneNumber}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="otp" className="label">
              Verification Code
            </label>
            <input
              type="text"
              id="otp"
              value={otpCode}
              onChange={handleOTPChange}
              placeholder="Enter OTP"
              className={`input-field text-center text-2xl tracking-widest ${isValid ? 'border-green-500' : otpCode ? 'border-red-500' : ''}`}
              maxLength="6"
              required
            />
            {otpCode && !isValid && (
              <p className="text-red-500 text-sm mt-1">
                Please enter a valid verification code
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`w-full flex items-center justify-center py-3 px-4 rounded-lg font-semibold transition-colors ${
              isValid && !isLoading
                ? 'bg-secondary-600 hover:bg-secondary-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                Verify Code
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </button>
        </form>

        <div className="mt-4 space-y-3">
          {/* Timer */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Code expires in: <span className="font-semibold text-primary-600">{formatTime(timeLeft)}</span>
            </p>
          </div>

          {/* Resend Button */}
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={timeLeft > 0}
              className={`text-sm ${
                timeLeft > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-primary-600 hover:text-primary-700 cursor-pointer'
              }`}
            >
              {timeLeft > 0 ? 'Resend code in ' + formatTime(timeLeft) : 'Resend code'}
            </button>
          </div>

          {/* Back Button */}
          <div className="text-center">
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary-600 hover:text-secondary-600 hover:bg-secondary-600/10 border border-primary-600/30 hover:border-secondary-600/50 rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Change phone number
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OTPVerification
